import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { creditosFinanceiros, CreditoFinanceiro } from '@/mocks/creditosFinanceiros';
import { calcularPontosFinanceiro } from '@/utils/pontosFinanceiro';

interface FinancialCardProps {
  tipo: 'pix' | 'pagamento' | 'cashback';
  imagem: string;
  onResgatar: (creditoId?: string) => void;
  dataTour?: string;
  dataTourBtn?: string;
}

function getDiasParaExpirar(credito: CreditoFinanceiro): number {
  const dataCredito = new Date(credito.dataCredito);
  const dataExpiracao = new Date(dataCredito);
  dataExpiracao.setDate(dataExpiracao.getDate() + Math.min(credito.diasExpiracao, 360));
  const hoje = new Date();
  return Math.ceil((dataExpiracao.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
}

function getDataExpiracao(credito: CreditoFinanceiro): Date {
  const dataCredito = new Date(credito.dataCredito);
  const dataExpiracao = new Date(dataCredito);
  dataExpiracao.setDate(dataExpiracao.getDate() + Math.min(credito.diasExpiracao, 360));
  return dataExpiracao;
}

const CARD_CONFIG = {
  pix: {
    nome: 'PIX',
    descricao: 'Resgate via PIX com valor personalizado',
    tagBase: 'PIX Personalizado',
    acaoLabel: (valor: number) => `Receber R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} via PIX`,
  },
  pagamento: {
    nome: 'Pagamento de Contas',
    descricao: 'Pagamento de contas com valor personalizado',
    tagBase: 'Pagamento Personalizado',
    acaoLabel: (valor: number) => `Pagar conta de R$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
  },
  cashback: {
    nome: 'CashBack',
    descricao: 'Receba de volta parte dos seus pontos em compras parceiras',
    tagBase: 'Cashback',
    acaoLabel: () => 'Em breve disponível',
  },
};

export default function FinancialCard({ tipo, imagem, onResgatar, dataTour, dataTourBtn }: FinancialCardProps) {
  const navigate = useNavigate();
  const config = CARD_CONFIG[tipo];
  const isCashback = tipo === 'cashback';

  // Para cashback não há seleção de crédito
  const [creditoSelecionadoId, setCreditoSelecionadoId] = useState<string>(
    !isCashback && creditosFinanceiros.length > 0 ? creditosFinanceiros[0].id : ''
  );

  const creditoSelecionado = creditosFinanceiros.find(c => c.id === creditoSelecionadoId) ?? null;
  const diasParaExpirar = creditoSelecionado ? getDiasParaExpirar(creditoSelecionado) : null;
  const dataExpiracao = creditoSelecionado ? getDataExpiracao(creditoSelecionado) : null;
  const dataCredito = creditoSelecionado ? new Date(creditoSelecionado.dataCredito) : null;
  const expirado = diasParaExpirar !== null && diasParaExpirar <= 0;
  const urgente = diasParaExpirar !== null && diasParaExpirar > 0 && diasParaExpirar <= 60;
  const muitoUrgente = diasParaExpirar !== null && diasParaExpirar > 0 && diasParaExpirar <= 30;

  const pontos = creditoSelecionado ? calcularPontosFinanceiro(creditoSelecionado.valor) : 0;

  const borderClass = muitoUrgente
    ? 'border-red-400 ring-2 ring-red-400'
    : urgente
    ? 'border-amber-400 ring-2 ring-amber-400'
    : 'border-slate-200';

  return (
    <div
      data-tour={dataTour}
      className={`bg-white rounded-xl overflow-hidden border hover:shadow-xl transition-all hover:-translate-y-1 group ${borderClass}`}
    >
      {/* Imagem */}
      <div className="relative h-48 overflow-hidden bg-slate-100">
        <img
          src={imagem}
          alt={config.nome}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
        />
      </div>

      {/* Conteúdo */}
      <div className="p-5 space-y-3">
        {/* Título e descrição */}
        <div>
          <h3 className="text-lg font-bold text-slate-900">
            {isCashback ? config.nome : (creditoSelecionado ? `${config.nome} — ${creditoSelecionado.descricao}` : config.nome)}
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            {isCashback
              ? config.descricao
              : creditoSelecionado
              ? `Campanha: ${creditoSelecionado.campanha} • ${config.descricao}`
              : config.descricao}
          </p>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
            {config.tagBase}
          </span>
          {creditoSelecionado && (
            <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
              {creditoSelecionado.campanha}
            </span>
          )}
        </div>

        {/* Seletor de crédito */}
        {!isCashback && creditosFinanceiros.length > 0 && (
          <div style={{
            borderRadius: 14,
            border: '2px solid #f97316',
            background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
            padding: '14px 16px',
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              fontWeight: 800,
              color: '#9a3412',
              marginBottom: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
            }}>
              <span style={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f97316',
                borderRadius: '50%',
                flexShrink: 0,
              }}>
                <i className="ri-list-check" style={{ color: 'white', fontSize: 12 }}></i>
              </span>
              Selecione o crédito para resgatar
            </label>
            <div style={{ position: 'relative' }}>
              <select
                value={creditoSelecionadoId}
                onChange={e => setCreditoSelecionadoId(e.target.value)}
                style={{
                  width: '100%',
                  paddingLeft: 14,
                  paddingRight: 44,
                  paddingTop: 10,
                  paddingBottom: 10,
                  border: '2px solid #f97316',
                  borderRadius: 10,
                  fontSize: 13,
                  color: '#1e293b',
                  background: 'white',
                  cursor: 'pointer',
                  fontWeight: 600,
                  appearance: 'none',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              >
                {creditosFinanceiros.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.descricao} — R$ {c.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} ({c.campanha})
                  </option>
                ))}
              </select>
              <div style={{
                pointerEvents: 'none',
                position: 'absolute',
                top: '50%',
                right: 10,
                transform: 'translateY(-50%)',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f97316',
                borderRadius: '50%',
              }}>
                <i className="ri-arrow-down-s-line" style={{ color: 'white', fontSize: 16 }}></i>
              </div>
            </div>
          </div>
        )}

        {/* Ação e pontos */}
        {!isCashback && creditoSelecionado && (
          <div className="pt-1">
            <p className="text-sm text-slate-700 font-medium">
              {config.acaoLabel(creditoSelecionado.valor)}
            </p>
            <p className="text-3xl font-bold text-orange-600 mt-0.5">
              {pontos.toLocaleString('pt-BR')}
              <span className="text-base text-slate-500 ml-1 font-normal">pts</span>
            </p>
          </div>
        )}

        {/* Data de crédito e expiração */}
        {!isCashback && creditoSelecionado && dataCredito && dataExpiracao && (
          <div className="pt-2 border-t border-slate-100 space-y-2">
            {/* Creditado em */}
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-slate-500"></i>
              </div>
              <span>
                Creditado em{' '}
                <strong className="text-slate-800">{dataCredito.toLocaleDateString('pt-BR')}</strong>
              </span>
              <button
                onClick={() =>
                  navigate(
                    `/premiado/extrato?busca=${encodeURIComponent(dataCredito.toLocaleDateString('pt-BR'))}&tipo=bonus`
                  )
                }
                className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 text-xs font-semibold hover:bg-teal-100 transition-colors cursor-pointer whitespace-nowrap border border-teal-200"
              >
                <i className="ri-external-link-line text-sm"></i>
                Ver no extrato
              </button>
            </div>

            {/* Expiração */}
            <div
              className={`flex items-center gap-2 text-sm font-medium ${
                expirado || muitoUrgente
                  ? 'text-red-600'
                  : urgente
                  ? 'text-amber-600'
                  : 'text-slate-600'
              }`}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i
                  className={`${
                    expirado || muitoUrgente
                      ? 'ri-time-line text-red-500'
                      : urgente
                      ? 'ri-alarm-warning-line text-amber-500'
                      : 'ri-timer-line text-slate-500'
                  }`}
                ></i>
              </div>
              {expirado ? (
                <span className="text-red-600 font-semibold">Crédito expirado</span>
              ) : (
                <span>
                  Expira em{' '}
                  <strong>
                    {diasParaExpirar} dia{diasParaExpirar !== 1 ? 's' : ''}
                  </strong>{' '}
                  ({dataExpiracao.toLocaleDateString('pt-BR')})
                </span>
              )}
            </div>

            {/* Badge de alerta */}
            {urgente && !expirado && (
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold ${
                  muitoUrgente
                    ? 'bg-red-100 text-red-700 border border-red-200 animate-pulse'
                    : 'bg-amber-100 text-amber-700 border border-amber-200'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${muitoUrgente ? 'ri-alarm-warning-fill' : 'ri-error-warning-fill'}`}></i>
                </div>
                <span>
                  {muitoUrgente
                    ? '⚠️ Atenção! Menos de 30 dias para expirar'
                    : '⏰ Expira em menos de 60 dias - Resgate já!'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Cashback — em breve */}
        {isCashback && (
          <div className="pt-1">
            <p className="text-sm text-slate-500 italic">Funcionalidade em breve disponível</p>
          </div>
        )}

        {/* Botão Resgatar */}
        <button
          data-tour={dataTourBtn}
          onClick={() => onResgatar(creditoSelecionadoId || undefined)}
          disabled={isCashback || expirado || (!isCashback && !creditoSelecionadoId)}
          className={`w-full py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${
            !isCashback && !expirado && creditoSelecionadoId
              ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:shadow-lg hover:from-orange-600 hover:to-orange-700'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          {isCashback ? (
            <>
              <i className="ri-time-line"></i>
              Em Breve
            </>
          ) : expirado ? (
            <>
              <i className="ri-lock-line"></i>
              Crédito Expirado
            </>
          ) : (
            <>
              <i className="ri-gift-line"></i>
              Resgatar Agora
            </>
          )}
        </button>
      </div>
    </div>
  );
}
